import { useEffect } from "react";
import { gsap, prefersReducedMotion } from "@/lib/gsapSetup";

/**
 * GSAP-driven horizontal ribbon (no native overflow scroll).
 *
 * Input routing (KL-003):
 * - Vertical wheel / vertical trackpad → page scroll (never preventDefault / never stop Lenis)
 * - Horizontally dominant trackpad → move cards only
 * - Pointer/touch horizontal drag → move cards; Lenis paused only during confirmed horizontal drag
 * - Vertical touch → page scroll
 */
export default function useHorizontalRibbon(viewportRef, trackRef, { enabled = true } = {}) {
  useEffect(() => {
    const viewport = viewportRef.current;
    const track = trackRef.current;
    if (!viewport || !track || !enabled) return undefined;

    const reduced = prefersReducedMotion();
    const state = { x: 0 };
    let targetX = 0;
    let maxX = 0;
    let lenisHeld = false;
    let suppressClickTO = 0;

    // Horizontal intent must clearly dominate vertical (conservative threshold).
    const HORIZONTAL_RATIO = 1.15;
    const AXIS_THRESHOLD = 8;
    const DRAG_THRESHOLD = 6;

    // Pointer / mouse / pen drag
    let pointerId = null;
    let pointerActive = false;
    let dragging = false;
    let suppressClick = false;
    let dragStartX = 0;
    let dragStartY = 0;
    let dragOrigin = 0;
    let lastDragX = 0;
    let lastDragT = 0;
    let dragVel = 0;

    // Touch gesture (separate from PointerEvent touch — keep touch* for older Safari)
    let touching = false;
    let axisLocked = null; // null | "x" | "y"
    let touchX = 0;
    let touchY = 0;
    let touchStartX = 0;
    let touchStartY = 0;
    let touchVel = 0;
    let touchLastT = 0;

    const getLenis = () => window.__lenis ?? null;

    /** Pause Lenis only during a confirmed horizontal pointer/touch drag. Idempotent. */
    const holdLenis = () => {
      const lenis = getLenis();
      if (!lenis || lenisHeld) return;
      lenis.stop();
      lenisHeld = true;
    };

    /** Resume Lenis immediately. Idempotent. Clears any prior delayed resume. */
    const releaseLenis = () => {
      const lenis = getLenis();
      if (lenis && lenisHeld) lenis.start();
      lenisHeld = false;
    };

    const measure = () => {
      maxX = Math.max(0, track.scrollWidth - viewport.clientWidth);
      targetX = gsap.utils.clamp(-maxX, 0, targetX);
      state.x = gsap.utils.clamp(-maxX, 0, state.x);
      gsap.set(track, { x: state.x });
    };

    const cardStride = () => {
      const card = track.querySelector("[data-service-card]");
      if (!card) return viewport.clientWidth * 0.3;
      const style = window.getComputedStyle(track);
      const gap = parseFloat(style.columnGap || style.gap) || 16;
      return card.getBoundingClientRect().width + gap;
    };

    const tweenTo = (next, duration = 1.2) => {
      targetX = gsap.utils.clamp(-maxX, 0, next);
      gsap.to(state, {
        x: targetX,
        duration: reduced ? 0.01 : duration,
        ease: "power3.out",
        overwrite: true,
        onUpdate: () => gsap.set(track, { x: state.x }),
      });
    };

    const atStart = () => state.x >= -0.5;
    const atEnd = () => state.x <= -maxX + 0.5;

    const isHorizontalWheel = (e) => {
      const absX = Math.abs(e.deltaX);
      const absY = Math.abs(e.deltaY);
      return absX > absY * HORIZONTAL_RATIO && absX > 0;
    };

    /**
     * Wheel / trackpad:
     * - Vertical or ambiguous → do nothing (let Lenis / page scroll)
     * - Horizontally dominant → move track; preventDefault only when we consume
     * - Never stop Lenis for wheel
     * - Never map deltaY into horizontal movement
     */
    const onWheel = (e) => {
      measure();
      if (maxX <= 1) return;
      if (!isHorizontalWheel(e)) return;

      let d = e.deltaX;
      if (d === 0) return;

      const goingForward = d > 0;
      // At bounds: do not consume — allow page scroll / other handlers
      if ((goingForward && atEnd()) || (!goingForward && atStart())) return;

      e.preventDefault();

      if (e.deltaMode === 1) d *= 18;
      if (e.deltaMode === 2) d *= viewport.clientHeight;

      const stride = cardStride();
      const cards = gsap.utils.clamp(1.6, 2.85, Math.abs(d) / 90 + 1.4);
      const move = Math.sign(d) * stride * cards;
      tweenTo(targetX - move, reduced ? 0.01 : 1.25);
    };

    const endTouchGesture = () => {
      if (!touching) return;
      const wasHorizontal = axisLocked === "x";
      touching = false;
      axisLocked = null;
      if (!wasHorizontal) {
        releaseLenis();
        return;
      }
      const coast = touchVel * 22;
      tweenTo(targetX + coast, reduced ? 0.01 : 1.05);
      releaseLenis();
    };

    const onTouchStart = (e) => {
      if (e.touches.length !== 1) return;
      measure();
      touching = true;
      axisLocked = null;
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
      touchX = touchStartX;
      touchY = touchStartY;
      touchVel = 0;
      touchLastT = performance.now();
      gsap.killTweensOf(state);
      targetX = state.x;
    };

    const onTouchMove = (e) => {
      if (!touching || maxX <= 1) return;
      const x = e.touches[0].clientX;
      const y = e.touches[0].clientY;
      const dxFromStart = x - touchStartX;
      const dyFromStart = y - touchStartY;
      const now = performance.now();

      if (!axisLocked) {
        if (Math.abs(dxFromStart) < AXIS_THRESHOLD && Math.abs(dyFromStart) < AXIS_THRESHOLD) {
          return;
        }
        // Lock once: horizontal only when clearly dominant; otherwise vertical (page scroll)
        axisLocked =
          Math.abs(dxFromStart) > Math.abs(dyFromStart) * HORIZONTAL_RATIO ? "x" : "y";
        if (axisLocked === "y") {
          releaseLenis();
          return;
        }
      }

      if (axisLocked === "y") return;

      const dx = x - touchX;
      const next = gsap.utils.clamp(-maxX, 0, state.x + dx);
      // Outward against a hard bound: do not preventDefault (avoid trapping scroll)
      if (next === state.x && ((dx > 0 && atStart()) || (dx < 0 && atEnd()))) {
        touchX = x;
        touchY = y;
        touchLastT = now;
        return;
      }

      e.preventDefault();
      holdLenis();

      const dt = Math.max(1, now - touchLastT);
      touchVel = (dx / dt) * 16;
      touchLastT = now;
      touchX = x;
      touchY = y;

      targetX = next;
      state.x = targetX;
      gsap.set(track, { x: state.x });
    };

    const onTouchEnd = () => {
      endTouchGesture();
    };

    const endPointerDrag = (e) => {
      if (!pointerActive) return;
      const id = e?.pointerId ?? pointerId;
      const wasDragging = dragging;

      if (wasDragging && id != null) {
        try {
          if (viewport.hasPointerCapture?.(id)) {
            viewport.releasePointerCapture(id);
          }
        } catch {
          /* ignore */
        }
      }

      if (wasDragging) {
        tweenTo(targetX + dragVel * 18, reduced ? 0.01 : 1.1);
      }

      pointerActive = false;
      dragging = false;
      pointerId = null;
      releaseLenis();

      // Suppress click only after a meaningful drag so CTA / focus still work on taps
      if (wasDragging) {
        suppressClick = true;
        clearTimeout(suppressClickTO);
        suppressClickTO = window.setTimeout(() => {
          suppressClick = false;
          suppressClickTO = 0;
        }, 0);
      }
    };

    const onPointerDown = (e) => {
      // Touch handled via touch* listeners for axis locking + touch-action pan-y
      if (e.pointerType === "touch") return;
      if (e.button !== 0) return;
      measure();
      if (maxX <= 1) return;

      pointerActive = true;
      dragging = false;
      pointerId = e.pointerId;
      dragStartX = e.clientX;
      dragStartY = e.clientY;
      dragOrigin = state.x;
      lastDragX = e.clientX;
      lastDragT = performance.now();
      dragVel = 0;
      // Do NOT hold Lenis or capture until horizontal drag is confirmed
    };

    const onPointerMove = (e) => {
      if (!pointerActive || e.pointerId !== pointerId) return;

      const dxFromStart = e.clientX - dragStartX;
      const dyFromStart = e.clientY - dragStartY;

      if (!dragging) {
        if (
          Math.abs(dxFromStart) < DRAG_THRESHOLD &&
          Math.abs(dyFromStart) < DRAG_THRESHOLD
        ) {
          return;
        }
        // Require horizontal dominance before committing to a drag
        if (Math.abs(dxFromStart) <= Math.abs(dyFromStart) * HORIZONTAL_RATIO) {
          // Vertical / ambiguous — abandon pointer gesture; let page scroll
          pointerActive = false;
          pointerId = null;
          return;
        }
        dragging = true;
        gsap.killTweensOf(state);
        targetX = state.x;
        dragOrigin = state.x;
        try {
          viewport.setPointerCapture(e.pointerId);
        } catch {
          /* ignore */
        }
        holdLenis();
      }

      const now = performance.now();
      const dt = Math.max(1, now - lastDragT);
      dragVel = ((e.clientX - lastDragX) / dt) * 16;
      lastDragX = e.clientX;
      lastDragT = now;
      targetX = gsap.utils.clamp(-maxX, 0, dragOrigin + dxFromStart);
      state.x = targetX;
      gsap.set(track, { x: state.x });
    };

    const onPointerUp = (e) => {
      if (!pointerActive || (e.pointerId !== undefined && e.pointerId !== pointerId)) return;
      endPointerDrag(e);
    };

    const onPointerCancel = (e) => {
      if (!pointerActive || (e.pointerId !== undefined && e.pointerId !== pointerId)) return;
      endPointerDrag(e);
    };

    const onClickCapture = (e) => {
      if (!suppressClick) return;
      e.preventDefault();
      e.stopPropagation();
      suppressClick = false;
    };

    const onBlurOrHide = () => {
      if (touching) endTouchGesture();
      if (pointerActive) endPointerDrag({ pointerId });
      releaseLenis();
    };

    const onVisibility = () => {
      if (document.visibilityState === "hidden") onBlurOrHide();
    };

    measure();
    const ro = new ResizeObserver(() => measure());
    ro.observe(viewport);
    ro.observe(track);

    viewport.addEventListener("wheel", onWheel, { passive: false });
    viewport.addEventListener("touchstart", onTouchStart, { passive: true });
    viewport.addEventListener("touchmove", onTouchMove, { passive: false });
    viewport.addEventListener("touchend", onTouchEnd, { passive: true });
    viewport.addEventListener("touchcancel", onTouchEnd, { passive: true });
    viewport.addEventListener("pointerdown", onPointerDown);
    viewport.addEventListener("click", onClickCapture, true);
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    window.addEventListener("pointercancel", onPointerCancel);
    window.addEventListener("blur", onBlurOrHide);
    document.addEventListener("visibilitychange", onVisibility);

    const onLoad = () => measure();
    window.addEventListener("load", onLoad);
    requestAnimationFrame(measure);

    return () => {
      clearTimeout(suppressClickTO);
      if (touching) endTouchGesture();
      if (pointerActive) endPointerDrag({ pointerId });
      releaseLenis();
      ro.disconnect();
      gsap.killTweensOf(state);
      viewport.removeEventListener("wheel", onWheel);
      viewport.removeEventListener("touchstart", onTouchStart);
      viewport.removeEventListener("touchmove", onTouchMove);
      viewport.removeEventListener("touchend", onTouchEnd);
      viewport.removeEventListener("touchcancel", onTouchEnd);
      viewport.removeEventListener("pointerdown", onPointerDown);
      viewport.removeEventListener("click", onClickCapture, true);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("pointercancel", onPointerCancel);
      window.removeEventListener("blur", onBlurOrHide);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("load", onLoad);
    };
  }, [viewportRef, trackRef, enabled]);
}
